/**
 * Zod helpers + a global, i18n-aware error map (messages follow the UI language at validation time,
 * so schemas can be declared once at module level).
 *
 * @example
 *   const schema = z.object({ code: zCode(), nameAr: zText(), nameEn: zText(), email: zOptEmail(), isActive: z.boolean() });
 */
import i18n from 'i18next';
import { z, ZodIssueCode, type ZodErrorMap } from 'zod';

const errorMap: ZodErrorMap = (issue, ctx) => {
  const t = (k: string, o?: Record<string, unknown>) => i18n.t(k, o);
  switch (issue.code) {
    case ZodIssueCode.invalid_type:
      if (issue.received === 'undefined' || issue.received === 'null' || issue.received === 'nan') return { message: t('common:validation.required') };
      if (issue.expected === 'number') return { message: t('common:validation.number') };
      return { message: t('common:validation.invalid') };
    case ZodIssueCode.too_small:
      if (issue.type === 'string') return { message: Number(issue.minimum) <= 1 ? t('common:validation.required') : t('common:validation.minLength', { min: issue.minimum }) };
      if (issue.type === 'array') return { message: t('common:validation.minItems', { min: issue.minimum }) };
      return { message: t(issue.inclusive ? 'common:validation.min' : 'common:validation.gt', { min: issue.minimum }) };
    case ZodIssueCode.too_big:
      if (issue.type === 'string') return { message: t('common:validation.maxLength', { max: issue.maximum }) };
      return { message: t('common:validation.max', { max: issue.maximum }) };
    case ZodIssueCode.invalid_string:
      if (issue.validation === 'email') return { message: t('common:validation.email') };
      return { message: t('common:validation.format') };
    case ZodIssueCode.invalid_enum_value:
      return { message: t('common:validation.option') };
    case ZodIssueCode.invalid_date:
      return { message: t('common:validation.date') };
    default:
      return { message: ctx.defaultError };
  }
};

z.setErrorMap(errorMap);

/** required, trimmed text */
export const zText = (max = 200) => z.string().trim().min(1).max(max);
/** optional text ('' allowed; convert with `emptyToNull` before sending) */
export const zOptText = (max = 500) => z.string().max(max).nullable().optional();
/** business code: letters, digits, `. _ -` */
export const zCode = (max = 40) =>
  z
    .string()
    .trim()
    .min(1)
    .max(max)
    .regex(/^[A-Za-z0-9._-]+$/);
export const zOptEmail = () => z.union([z.literal(''), z.string().trim().email()]).nullable().optional();
export const zNum = () => z.number();
export const zOptNum = () => z.number().nullable().optional();
export const zDate = () => z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const zOptDate = () => z.string().nullable().optional();
export const zId = () => z.string().min(1);
export const zOptId = () => z.string().nullable().optional();

/** Top-level `''` → `null` (optional inputs) before sending to the API. */
export function emptyToNull<T extends Record<string, unknown>>(values: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(values)) out[k] = v === '' ? null : v;
  return out as T;
}

export { z };
